import * as React from "react";
import { FolderOpen, ChevronRight, Check, ChevronsUpDown, Plus } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface Category {
  id: string;
  name: string;
  type?: string;
  level2Id?: string;
  code?: string;
  [key: string]: any;
}

interface Level1 {
  id: string;
  code: string;
  name: string;
}

interface Level2 {
  id: string;
  level1_id: string;
  code: string;
  name: string;
  type: string;
}

interface CategorySelectorProps {
  categories: Category[];
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  allowFreeText?: boolean;
  freeTextValue?: string;
  onFreeTextChange?: (value: string) => void;
  label?: string;
  error?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
  onEnterPress?: () => void;
  filterType?: string;
  canAddNew?: boolean;
  onAddNew?: (name: string, type: string) => Promise<void>;
  required?: boolean;
}

export function CategorySelector({
  categories,
  value,
  onValueChange,
  placeholder = "Selecione um plano de contas",
  allowFreeText = false,
  freeTextValue = "",
  onFreeTextChange,
  label = "Plano de Contas",
  error,
  inputRef,
  onEnterPress,
  filterType,
  canAddNew = false,
  onAddNew,
  required = true
}: CategorySelectorProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const [level1List, setLevel1List] = React.useState<Level1[]>([]);
  const [level2List, setLevel2List] = React.useState<Level2[]>([]);
  const [showAddDialog, setShowAddDialog] = React.useState(false);
  const [newItemName, setNewItemName] = React.useState("");
  const [isAdding, setIsAdding] = React.useState(false);
  
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const internalInputRef = React.useRef<HTMLInputElement>(null);
  const dropdownListRef = React.useRef<HTMLDivElement>(null);
  const justSelectedRef = React.useRef(false);
  
  const activeInputRef = inputRef || internalInputRef;

  // Fetch Level1 and Level2 data
  React.useEffect(() => {
    if (!user) return;
    
    const fetchHierarchy = async () => {
      const [l1Result, l2Result] = await Promise.all([
        supabase.from('chart_of_accounts_level1').select('*').order('code'),
        supabase.from('chart_of_accounts_level2').select('*').order('code')
      ]);
      
      if (l1Result.data) setLevel1List(l1Result.data);
      if (l2Result.data) setLevel2List(l2Result.data);
    };
    
    fetchHierarchy();
  }, [user]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Build hierarchical structure - FIXED: Filter by Level 3 type first, then build hierarchy
  const hierarchicalData = React.useMemo(() => {
    const searchLower = searchValue.toLowerCase();
    
    // STEP 1: Filter Level 3 categories by type and search FIRST
    // This is the key fix - we filter by the Level 3 type, not Level 2 type
    const matchingCategories = categories.filter(cat => 
      !cat.hidden && 
      (!filterType || cat.type === filterType) &&
      (cat.name.toLowerCase().includes(searchLower) ||
       (cat.code && cat.code.toLowerCase().includes(searchLower)))
    );
    
    // STEP 2: Identify which Level 2 accounts have matching Level 3 categories
    const level2IdsWithMatchingN3 = new Set(
      matchingCategories
        .filter(cat => cat.level2Id)
        .map(cat => cat.level2Id)
    );
    
    // STEP 3: Filter Level 2 to only those with matching Level 3
    const relevantLevel2 = level2List.filter(l2 => 
      level2IdsWithMatchingN3.has(l2.id)
    );
    
    // STEP 4: Identify which Level 1 accounts have relevant Level 2
    const level1IdsWithLevel2 = new Set(relevantLevel2.map(l2 => l2.level1_id));
    const relevantLevel1 = level1List.filter(l1 => level1IdsWithLevel2.has(l1.id));
    
    // STEP 5: Build hierarchy sorted by code
    const sortedLevel1 = [...relevantLevel1].sort((a, b) =>
      a.code.localeCompare(b.code, undefined, { numeric: true })
    );
    
    const result: {
      level1: Level1;
      children: {
        level2: Level2;
        categories: Category[];
      }[];
    }[] = [];
    
    for (const l1 of sortedLevel1) {
      // Sort Level 2 by code within each Level 1
      const l2Children = relevantLevel2
        .filter(l2 => l2.level1_id === l1.id)
        .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
      
      const l2WithCategories = l2Children.map(l2 => ({
        level2: l2,
        // Sort Level 3 by code within each Level 2
        categories: matchingCategories
          .filter(cat => cat.level2Id === l2.id)
          .sort((a, b) => (a.code || '').localeCompare(b.code || '', undefined, { numeric: true }))
      })).filter(item => item.categories.length > 0);
      
      if (l2WithCategories.length > 0) {
        result.push({
          level1: l1,
          children: l2WithCategories
        });
      }
    }
    
    // Unlinked categories (those without level2Id)
    const unlinkedCategories = matchingCategories
      .filter(cat => !cat.level2Id)
      .sort((a, b) => (a.code || '').localeCompare(b.code || '', undefined, { numeric: true }));
    
    return { hierarchy: result, unlinked: unlinkedCategories };
  }, [level1List, level2List, categories, searchValue, filterType]);

  // Flatten for keyboard navigation
  const flatItems = React.useMemo(() => {
    const items: Category[] = [];
    for (const l1Group of hierarchicalData.hierarchy) {
      for (const l2Group of l1Group.children) {
        items.push(...l2Group.categories);
      }
    }
    items.push(...hierarchicalData.unlinked);
    return items;
  }, [hierarchicalData]);

  const selectedItem = categories.find(cat => cat.id === value);
  const displayValue = open ? searchValue : (selectedItem ? (selectedItem.code ? `${selectedItem.code} - ${selectedItem.name}` : selectedItem.name) : "");

  // Reset highlighted index when filtered items change
  React.useEffect(() => {
    setHighlightedIndex(-1);
  }, [flatItems.length, searchValue]);

  // Scroll to highlighted item
  React.useEffect(() => {
    if (highlightedIndex >= 0 && dropdownListRef.current) {
      const items = dropdownListRef.current.querySelectorAll('[data-item-index]');
      const item = items[highlightedIndex] as HTMLElement;
      if (item) {
        item.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const handleSelectItem = (itemId: string, advanceToNext: boolean = false) => {
    justSelectedRef.current = true;
    onValueChange(itemId);
    setOpen(false);
    setSearchValue("");
    setHighlightedIndex(-1);
    
    setTimeout(() => {
      justSelectedRef.current = false;
    }, 100);
    
    if (advanceToNext) {
      setTimeout(() => onEnterPress?.(), 50);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setSearchValue("");
      setHighlightedIndex(-1);
    } else if (e.key === 'Tab') {
      if (open && highlightedIndex >= 0 && highlightedIndex < flatItems.length) {
        handleSelectItem(flatItems[highlightedIndex].id);
      }
      setOpen(false);
      setSearchValue("");
      setHighlightedIndex(-1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setHighlightedIndex(prev => 
        prev < flatItems.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) setOpen(true);
      setHighlightedIndex(prev => 
        prev > 0 ? prev - 1 : flatItems.length - 1
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && highlightedIndex >= 0 && highlightedIndex < flatItems.length) {
        handleSelectItem(flatItems[highlightedIndex].id, true);
      } else if (!open || flatItems.length === 0) {
        onEnterPress?.();
      }
    }
  };

  const handleAddNew = async (name: string) => {
    if (onAddNew && filterType) {
      setIsAdding(true);
      try {
        await onAddNew(name, filterType);
        setShowAddDialog(false);
        setNewItemName("");
        toast({ title: "Item adicionado com sucesso!" });
      } catch (error) {
        toast({ variant: "destructive", title: "Erro ao adicionar item" });
      } finally {
        setIsAdding(false);
      }
    }
  };

  let globalIndex = -1;

  return (
    <div className="space-y-2">
      <Label className={cn(error && "text-destructive")}>
        {label} {required && "*"}
      </Label>
      <div className="relative" ref={dropdownRef}>
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <FolderOpen className="h-4 w-4 text-muted-foreground" />
          </div>
          <Input
            ref={activeInputRef}
            value={displayValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              setHighlightedIndex(-1);
              if (!open) setOpen(true);
            }}
            onFocus={() => {
              if (!justSelectedRef.current) {
                setOpen(true);
              }
            }}
            onKeyDown={handleInputKeyDown}
            placeholder={placeholder}
            className={cn(
              "w-full h-10 bg-background pl-9 pr-8",
              error && "border-destructive focus-visible:ring-destructive"
            )}
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
            <ChevronsUpDown className="h-4 w-4 text-muted-foreground opacity-50" />
          </div>
        </div>

        {/* Hierarchical Dropdown */}
        {open && (
          <div 
            ref={dropdownListRef}
            className="absolute top-full left-0 right-0 z-50 mt-1 bg-background border rounded-md shadow-lg max-h-80 overflow-auto min-w-[450px]"
          >
            {flatItems.length === 0 && hierarchicalData.hierarchy.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Nenhum plano de contas encontrado.
              </div>
            ) : (
              <>
                {/* Hierarchical structure */}
                {hierarchicalData.hierarchy.map((l1Group) => (
                  <div key={l1Group.level1.id} className="border-b last:border-b-0">
                    {/* Level 1 Header - Gray, non-selectable */}
                    <div className="px-3 py-2 bg-muted/70 font-semibold text-sm text-muted-foreground flex items-center gap-2 sticky top-0 cursor-default select-none">
                      <span className="text-xs font-mono text-muted-foreground/80 bg-muted px-1.5 py-0.5 rounded">
                        {l1Group.level1.code}
                      </span>
                      <span className="text-muted-foreground">{l1Group.level1.name}</span>
                    </div>
                    
                    {l1Group.children.map((l2Group) => (
                      <div key={l2Group.level2.id}>
                        {/* Level 2 Header - Gray, non-selectable */}
                        <div className="px-3 py-1.5 bg-muted/40 text-sm text-muted-foreground flex items-center gap-2 pl-6 cursor-default select-none">
                          <ChevronRight className="h-3 w-3 text-muted-foreground/60" />
                          <span className="text-xs font-mono text-muted-foreground/80 bg-muted px-1.5 py-0.5 rounded">
                            {l2Group.level2.code}
                          </span>
                          <span className="text-muted-foreground/90">{l2Group.level2.name}</span>
                        </div>
                        
                        {/* Level 3 Items (Selectable) */}
                        {l2Group.categories.map((cat) => {
                          globalIndex++;
                          const currentIndex = globalIndex;
                          return (
                            <div
                              key={cat.id}
                              data-item-index={currentIndex}
                              onClick={() => handleSelectItem(cat.id)}
                              className={cn(
                                "flex items-center justify-between px-3 py-2 cursor-pointer pl-12",
                                currentIndex === highlightedIndex 
                                  ? "bg-accent text-accent-foreground" 
                                  : "hover:bg-muted/50"
                              )}
                            >
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span className="font-medium truncate">
                                  {cat.code ? `${cat.code} - ${cat.name}` : cat.name}
                                </span>
                              </div>
                              <Check
                                className={cn(
                                  "h-4 w-4 flex-shrink-0",
                                  value === cat.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                ))}
                
                {/* Unlinked categories */}
                {hierarchicalData.unlinked.length > 0 && (
                  <div className="border-t">
                    <div className="px-3 py-2 bg-muted/50 font-semibold text-sm text-muted-foreground">
                      Não classificados
                    </div>
                    {hierarchicalData.unlinked.map((cat) => {
                      globalIndex++;
                      const currentIndex = globalIndex;
                      return (
                        <div
                          key={cat.id}
                          data-item-index={currentIndex}
                          onClick={() => handleSelectItem(cat.id)}
                          className={cn(
                            "flex items-center justify-between px-3 py-2 cursor-pointer pl-6",
                            currentIndex === highlightedIndex 
                              ? "bg-accent text-accent-foreground" 
                              : "hover:bg-muted/50"
                          )}
                        >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className="font-medium truncate">
                              {cat.code ? `${cat.code} - ${cat.name}` : cat.name}
                            </span>
                            <Badge variant="secondary" className="text-xs">
                              {cat.type}
                            </Badge>
                          </div>
                          <Check
                            className={cn(
                              "h-4 w-4 flex-shrink-0",
                              value === cat.id ? "opacity-100" : "opacity-0"
                            )}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Add new option */}
                {canAddNew && onAddNew && (
                  <div
                    onClick={() => {
                      setShowAddDialog(true);
                      setNewItemName(searchValue.trim());
                      setOpen(false);
                    }}
                    className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted border-t"
                  >
                    <Plus className="h-4 w-4 text-primary" />
                    <span className="text-sm text-primary font-medium">
                      {searchValue.trim() 
                        ? `Adicionar Novo Plano de Contas: "${searchValue.trim()}"` 
                        : "Adicionar Novo Plano de Contas"
                      }
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {error && (
        <div className="text-sm text-destructive">{error}</div>
      )}

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar Novo Plano de Contas</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder="Nome do plano de contas"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newItemName.trim()) {
                    handleAddNew(newItemName.trim());
                  }
                }}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={() => handleAddNew(newItemName.trim())}
                disabled={!newItemName.trim() || isAdding}
              >
                {isAdding ? "Adicionando..." : "Adicionar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}